import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { AlertTriangle, RotateCcw, Loader2, RefreshCw, CheckCircle2, Eye, Package, Truck, ArrowRight, ArrowLeft, X, AlertCircle, Upload, FileSpreadsheet, Camera, Search, CheckSquare, Square, ChevronDown, Menu } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabaseClient';
import { SidebarTrigger } from './ui/sidebar';

// Import sub-components for the new loader workflow
import LoaderPalletList from './loader/LoaderPalletList';
import Loader3DView from './loader/Loader3DView';
import LoaderProgressTracker from './loader/LoaderProgressTracker';
import PalletPrecheckView from './loader/PalletPrecheckView';

/**
 * NewLoaderPlanView - Complete rework for mobile-optimized loading workflow
 * 
 * Workflow:
 * 1. Plan Selection - Select from saved loads/create new plan
 * 2. Pallet Validation - Review proposed order, mark issues (missing, too tall, not top-loadable)
 * 3. Stack-by-Stack Loading - 3D view of placement, right stack to middle, real-time solver integration
 * 4. Progress Tracking - Visual progress with locked/unplaced pallet management
 */
export default function NewLoaderPlanView() {
  const { authenticatedFetch } = useAuth();
  const { savedLoads, getSavedLoadsByDateRange, lines, trucks } = useData();

  // ==================== STATE MANAGEMENT ====================
  
  // Plan and loading state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Workflow phases: 'selection' -> 'precheck' -> 'validation' -> 'loading' -> 'complete'
  const [currentPhase, setCurrentPhase] = useState('selection');
  
  // Plan data structure
  const [planData, setPlanData] = useState({
    planId: null,
    pallets: [], // All pallets with positions, dimensions, status
    proposedOrder: [], // Initial solver-generated order
    currentOrder: [], // Current order (updated after issues marked)
    placedPallets: [], // Pallets that have been physically loaded
    problemPallets: [], // Pallets marked with issues
    stacks: [], // Organized by stacks for 3D view
    truckDimensions: null,
    hasExistingArrangement: false,
    progress: {
      currentStackIndex: 0,
      currentPalletInStack: 0,
      totalStacks: 0,
      totalPallets: 0,
      placedCount: 0
    }
  });
  
  // Mobile-responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // 3D view state
  const [selectedPalletId, setSelectedPalletId] = useState(null);
  
  // Date/load selection
  const todayStr = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [showAllDates, setShowAllDates] = useState(false);

  // Import state
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showScannerImportModal, setShowScannerImportModal] = useState(false);
  const [scannedPallets, setScannedPallets] = useState([]);
  const [selectedScannedPallets, setSelectedScannedPallets] = useState(new Set());
  const [scannerSearchQuery, setScannerSearchQuery] = useState('');
  const [loadingScannedPallets, setLoadingScannedPallets] = useState(false);
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(true); // Default to showing only unassigned
  const importDropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Ongoing loads from localStorage
  const [ongoingLoads, setOngoingLoads] = useState([]);

  // ==================== RESPONSIVE HANDLING ======================================
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true); // Auto-open on desktop
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ==================== ONGOING LOADS PERSISTENCE ====================
  
  // Load ongoing loads from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('lodia_ongoing_loads');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Filter out old entries (older than 7 days)
        const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const recent = parsed.filter(l => l.updatedAt > weekAgo);
        setOngoingLoads(recent);
        // Clean up old entries
        if (recent.length !== parsed.length) {
          localStorage.setItem('lodia_ongoing_loads', JSON.stringify(recent));
        }
      }
    } catch (e) {
      console.warn('Failed to load ongoing loads:', e);
    }
  }, []);

  // Save progress to localStorage when in loading phase (even before placing pallets)
  useEffect(() => {
    // Support both selectedPlan (from saved loads) and planData.planId (from imports)
    const planId = selectedPlan?.id || planData.planId;
    if (planId && currentPhase === 'loading') {
      try {
        const isImported = !selectedPlan && planData.planId?.startsWith('imported-');
        const loadProgress = {
          planId: planId,
          planName: selectedPlan?.name || planData.planName || `Imported Load`,
          lineId: selectedPlan?.line_id || null,
          truckId: selectedPlan?.truck_id || null,
          loadDate: selectedPlan?.load_date || selectedPlan?.date || new Date().toISOString().slice(0, 10),
          placedPallets: planData.placedPallets,
          problemPallets: planData.problemPallets.map(p => ({ id: p.id, reason: p.reason })),
          totalPallets: planData.pallets.length,
          currentPhase,
          isImported,
          // For imported loads, store full pallet data so we can restore
          palletData: isImported ? planData.pallets : undefined,
          truckDimensions: isImported ? planData.truckDimensions : undefined,
          updatedAt: Date.now()
        };
        
        setOngoingLoads(prev => {
          const filtered = prev.filter(l => l.planId !== planId);
          const updated = [loadProgress, ...filtered].slice(0, 10); // Keep max 10
          localStorage.setItem('lodia_ongoing_loads', JSON.stringify(updated));
          return updated;
        });
      } catch (e) {
        console.warn('Failed to save ongoing load:', e);
      }
    }
  }, [selectedPlan, planData.placedPallets, planData.problemPallets, planData.pallets, planData.planId, planData.planName, planData.truckDimensions, currentPhase]);

  // Remove completed loads from ongoing list
  useEffect(() => {
    const planId = selectedPlan?.id || planData.planId;
    if (currentPhase === 'complete' && planId) {
      setOngoingLoads(prev => {
        const filtered = prev.filter(l => l.planId !== planId);
        localStorage.setItem('lodia_ongoing_loads', JSON.stringify(filtered));
        return filtered;
      });
    }
  }, [currentPhase, selectedPlan, planData.planId]);

  // ==================== DATA LOADING ====================
  
  // Load saved loads for date selection
  useEffect(() => {
    (async () => {
      try {
        if (showAllDates) {
          const to = new Date();
          const from = new Date(Date.now() - 7 * 24 * 3600 * 1000);
          const fmt = (d) => d.toISOString().slice(0, 10);
          await getSavedLoadsByDateRange(fmt(from), fmt(to));
        } else {
          await getSavedLoadsByDateRange(selectedDate, selectedDate);
        }
      } catch (err) {
        console.error('Failed to load saved loads:', err);
      }
    })();
  }, [selectedDate, showAllDates, getSavedLoadsByDateRange]);

  // Close import dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(e.target)) {
        setShowImportDropdown(false);
      }
    };
    if (showImportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showImportDropdown]);

  // Filter and sort recent loads
  const recentLoads = useMemo(() => {
    let arr = Array.isArray(savedLoads) ? [...savedLoads] : [];
    if (!showAllDates) {
      arr = arr.filter(l => (l.load_date || l.date) === selectedDate);
    }
    arr.sort((a, b) => {
      const da = a.load_date || a.date || a.created_at || a.updated_at || '';
      const db = b.load_date || b.date || b.created_at || b.updated_at || '';
      return String(db).localeCompare(String(da));
    });
    return arr.slice(0, 50);
  }, [savedLoads, selectedDate, showAllDates]);

  // ==================== IMPORT FUNCTIONS ====================

  // Load scanned pallets from Supabase
  const loadScannedPallets = useCallback(async () => {
    setLoadingScannedPallets(true);
    try {
      const { data, error } = await supabase
        .from('scanned_pallet')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const mapped = (data || []).map(p => ({
        id: p.id,
        name: p.name || `Pallet ${p.id}`,
        width_cm: p.width_cm || 80,
        depth_cm: p.depth_cm || 120,
        height_cm: p.height_cm || 100,
        weight_kg: p.weight_kg || 300,
        attribute: p.attribute || 'none',
        destination: p.destination,
        shipment_id: p.shipment_id,
        sender: p.sender,
        receiver: p.receiver,
        imageUrl: p.image_url,
        labelImageUrl: p.label_image_url,
        created_at: p.created_at,
        // Assignment tracking
        load_id: p.load_id,
        assigned_at: p.assigned_at,
        isAssigned: !!p.load_id,
      }));
      setScannedPallets(mapped);
    } catch (err) {
      console.error('[NewLoaderPlanView] Error loading scanned pallets:', err);
    } finally {
      setLoadingScannedPallets(false);
    }
  }, []);

  // Open scanner import modal
  const openScannerImportModal = useCallback(() => {
    setShowImportDropdown(false);
    setShowScannerImportModal(true);
    setSelectedScannedPallets(new Set());
    setScannerSearchQuery('');
    loadScannedPallets();
  }, [loadScannedPallets]);

  // Toggle pallet selection
  const togglePalletSelection = useCallback((palletId) => {
    setSelectedScannedPallets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(palletId)) {
        newSet.delete(palletId);
      } else {
        newSet.add(palletId);
      }
      return newSet;
    });
  }, []);

  // Filter scanned pallets by search query and assignment status
  const filteredScannedPallets = useMemo(() => {
    let filtered = scannedPallets;
    
    // Filter by assignment status
    if (showOnlyUnassigned) {
      filtered = filtered.filter(p => !p.isAssigned);
    }
    
    // Filter by search query
    if (scannerSearchQuery.trim()) {
      const q = scannerSearchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        (p.name || '').toLowerCase().includes(q) ||
        (p.destination || '').toLowerCase().includes(q) ||
        (p.shipment_id || '').toLowerCase().includes(q)
      );
    }
    
    return filtered;
  }, [scannedPallets, scannerSearchQuery, showOnlyUnassigned]);

  // Count of unassigned pallets
  const unassignedCount = useMemo(() => 
    scannedPallets.filter(p => !p.isAssigned).length
  , [scannedPallets]);

  // Select all filtered pallets
  const selectAllFiltered = useCallback(() => {
    setSelectedScannedPallets(new Set(filteredScannedPallets.map(p => p.id)));
  }, [filteredScannedPallets]);

  // Create plan from imported pallets
  const createPlanFromImportedPallets = useCallback(async (pallets) => {
    if (!pallets || pallets.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      // Default truck dimensions
      const truckDimensions = { width: 2.45, depth: 12, height: 2.6 };

      // Convert pallets to solver format
      const solverPallets = pallets.map((p, idx) => ({
        id: p.id || `imported-${Date.now()}-${idx}`,
        name: p.name || `Pallet ${idx + 1}`,
        dimensions: {
          width: p.width || (p.width_cm ? p.width_cm / 100 : 0.8),
          depth: p.depth || (p.depth_cm ? p.depth_cm / 100 : 1.2),
          height: p.height || (p.height_cm ? p.height_cm / 100 : 1.0),
        },
        weight: p.weight || p.weight_kg || 300,
        attribute: p.attribute || 'none',
        notToploadable: p.notToploadable || false,
        locked: false,
        unit_id: 1,
        // Preserve metadata
        destination: p.destination,
        shipment_id: p.shipment_id,
        imageUrl: p.imageUrl || p.image_url,
        labelImageUrl: p.labelImageUrl || p.label_image_url,
      }));

      // Call solver to arrange pallets
      const solverPayload = {
        pallets: solverPallets,
        truckDimensions,
        vehicleId: 1,
        resetRows: true,
        solverVersion: 'stacked_v2'
      };

      let arrangement = solverPallets; // Fallback to input if solver fails
      let solverSuccess = false;

      try {
        console.log('[NewLoaderPlanView] Calling solver with', solverPallets.length, 'pallets, version:', 'stacked_v2');
        const response = await authenticatedFetch('/api/arrange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(solverPayload)
        });

        console.log('[NewLoaderPlanView] Solver response status:', response.status);
        
        if (response.ok) {
          const result = await response.json();
          console.log('[NewLoaderPlanView] Solver result:', result?.arrangement?.length, 'pallets arranged');
          if (result?.arrangement && result.arrangement.length > 0) {
            // Merge solver positions with original metadata
            arrangement = result.arrangement.map(p => {
              const original = solverPallets.find(op => op.id === p.id);
              return {
                ...p,
                destination: original?.destination,
                shipment_id: original?.shipment_id,
                imageUrl: original?.imageUrl,
                labelImageUrl: original?.labelImageUrl,
                isImportedPallet: true,
              };
            });
            solverSuccess = true;
          } else {
            console.warn('[NewLoaderPlanView] Solver returned empty or no arrangement');
          }
        } else {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error('[NewLoaderPlanView] Solver returned error:', response.status, errorText);
          setError(`Solver error: ${response.status}`);
        }
      } catch (solverErr) {
        console.error('[NewLoaderPlanView] Solver network/parse error:', solverErr);
        setError(`Solver connection failed: ${solverErr.message}`);
      }

      if (!solverSuccess) {
        console.warn('[NewLoaderPlanView] Using unpositioned fallback arrangement');
      }

      // Organize into stacks
      const stacks = organizeIntoStacks(arrangement);

      // Generate a human-readable name for imported loads
      const importedPlanName = `Scanner Import (${pallets.length} pallets)`;
      const generatedPlanId = `imported-${Date.now()}`;

      // Mark these pallets as assigned to this load in the database
      const palletIds = pallets.map(p => p.id).filter(id => typeof id === 'number');
      if (palletIds.length > 0) {
        try {
          await supabase
            .from('scanned_pallet')
            .update({ 
              load_id: generatedPlanId,
              assigned_at: new Date().toISOString()
            })
            .in('id', palletIds);
          console.log(`[NewLoaderPlanView] Marked ${palletIds.length} pallets as assigned to load ${generatedPlanId}`);
        } catch (dbErr) {
          console.warn('[NewLoaderPlanView] Failed to update pallet assignment:', dbErr);
        }
      }

      // Mark pallets in arrangement as imported (for badge display)
      const arrangementWithImportFlag = arrangement.map(p => ({
        ...p,
        isImportedPallet: true,
      }));

      // Set plan data
      setPlanData({
        planId: generatedPlanId,
        planName: importedPlanName,
        pallets: arrangementWithImportFlag,
        proposedOrder: arrangementWithImportFlag.map(p => p.id),
        currentOrder: arrangementWithImportFlag.map(p => p.id),
        placedPallets: [],
        problemPallets: [],
        stacks,
        truckDimensions,
        hasExistingArrangement: true,
        progress: {
          currentStackIndex: 0,
          currentPalletInStack: 0,
          totalStacks: stacks.length,
          totalPallets: arrangementWithImportFlag.length,
          placedCount: 0
        }
      });

      // Move to precheck phase
      setCurrentPhase('precheck');
      setShowScannerImportModal(false);

    } catch (err) {
      console.error('[NewLoaderPlanView] Error creating plan from import:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedFetch]);

  // Load all unassigned pallets directly (convenience function)
  const loadAllUnassigned = useCallback(() => {
    const unassignedPallets = scannedPallets.filter(p => !p.isAssigned);
    if (unassignedPallets.length === 0) return;
    createPlanFromImportedPallets(unassignedPallets);
  }, [scannedPallets, createPlanFromImportedPallets]);

  // Import selected scanned pallets
  const importSelectedScannedPallets = useCallback(() => {
    const selected = scannedPallets.filter(p => selectedScannedPallets.has(p.id));
    if (selected.length === 0) return;
    createPlanFromImportedPallets(selected);
  }, [scannedPallets, selectedScannedPallets, createPlanFromImportedPallets]);

  // Handle Excel file import
  const handleExcelImport = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setShowImportDropdown(false);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const [, ...dataRows] = rows; // Skip header row

        const validAttributes = { 
          'no attribute': 'none', 'none': 'none', 
          'medicine': 'medicine', 'food': 'food', 
          'dangerous': 'dangerous' 
        };

        const pallets = dataRows
          .filter(row => row && row.length > 0)
          .map((row, idx) => {
            const attrRaw = (row[5]?.toString().trim().toLowerCase()) || 'none';
            const attribute = validAttributes[attrRaw] || 'none';
            
            return {
              id: `excel-${Date.now()}-${idx}`,
              name: (row[0] ?? '').toString().trim() || `Pallet ${idx + 1}`,
              width: parseFloat(row[1]) || 0.8,
              depth: parseFloat(row[2]) || 1.2,
              height: parseFloat(row[3]) || 0.5,
              weight: parseFloat(row[4]) || 100,
              attribute,
              notToploadable: false,
            };
          });

        if (pallets.length > 0) {
          createPlanFromImportedPallets(pallets);
        } else {
          setError('No valid pallets found in Excel file');
          setLoading(false);
        }
      } catch (err) {
        console.error('[NewLoaderPlanView] Excel import error:', err);
        setError('Failed to parse Excel file');
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [createPlanFromImportedPallets]);

  // ==================== HELPER FUNCTIONS ====================
  
  // Heuristic retained only for gating non-essential backend POSTs (exceptions recording).
  const isBackendConfigured = useCallback(() => {
    const envBase = (process.env.REACT_APP_BACKEND_BASE_URL || process.env.REACT_APP_API_BASE_URL || '').trim();
    const host = typeof window !== 'undefined' ? (window.location?.host || '') : '';
    const onReactDev = /^(localhost|127\.0\.0\.1):3000$/i.test(host);
    if (envBase) return true;
    return !onReactDev;
  }, []);

  const resolveLineName = useCallback((lineId) => {
    const line = lines.find(l => String(l.line_id) === String(lineId));
    return line?.name || lineId || '—';
  }, [lines]);

  const resolveTruckName = useCallback((lineId, truckId) => {
    const list = trucks[lineId] || [];
    const truck = list.find(t => String(t.truck_id) === String(truckId));
    return truck?.name || truckId || '—';
  }, [trucks]);

  // ==================== PLAN PROCESSING ====================
  
  /**
   * Convert saved load to initial plan structure and get proposed order from solver
   */
  const processSavedLoadToPlan = useCallback(async (savedLoad) => {
    setLoading(true);
    setError(null);
    
    try {
      // Extract pallet and truck data from saved load
      const pallets = savedLoad.pallets_data || [];
      const truckData = savedLoad.truck_data || {};
      
      if (pallets.length === 0) {
        throw new Error('No pallets found in selected load');
      }

      // Build plan from saved data only; go to PRECHECK first (no solver yet)
      const arrangementFromSaved = pallets.map((p, idx) => ({
        id: p.id,
        name: p.name || `Pallet${p.id}`,
        dimensions: p.dimensions,
        weight: p.weight || 0,
        attribute: p.attribute || 'none',
        notToploadable: Boolean(p.notToploadable),
        rotation: p.rotation || 0,
        position: Array.isArray(p.position) || (p.position && typeof p.position === 'object') ? p.position : undefined,
        locked: false,
        unit_id: 1,
        __origIndex: idx,
        // Preserve image data for precheck display
        imageUrl: p.imageUrl || p.image_url,
        labelImageUrl: p.labelImageUrl || p.label_image_url,
        // Preserve metadata
        destination: p.destination,
        shipment_id: p.shipment_id,
        sender: p.sender,
        receiver: p.receiver,
      }));

      const hasExistingArrangement = arrangementFromSaved.some(p => Array.isArray(p.position) || (p.position && typeof p.position === 'object'));
      const stacksLocal = organizeIntoStacks(arrangementFromSaved);
      const plan = {
        planId: `plan_${Date.now()}`,
        pallets: arrangementFromSaved,
        proposedOrder: arrangementFromSaved.map(p => p.id),
        currentOrder: arrangementFromSaved.map(p => p.id),
        placedPallets: [],
        problemPallets: [],
        stacks: stacksLocal,
        truckDimensions: truckData.dimensions || { width: 2.45, depth: 12, height: 2.6 },
        hasExistingArrangement,
        progress: {
          currentStackIndex: 0,
          currentPalletInStack: 0,
          totalStacks: stacksLocal.length,
          totalPallets: arrangementFromSaved.length,
          placedCount: 0
        }
      };

      setPlanData(plan);
      setSelectedPlan(savedLoad);
      setCurrentPhase('precheck');

    } catch (err) {
      setError(err.message || 'Failed to process saved load');
      console.error('Error processing saved load:', err);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  /**
   * Resume an ongoing load from localStorage progress
   */
  const handleResumeOngoingLoad = useCallback(async (ongoingLoad) => {
    setLoading(true);
    setError(null);

    try {
      // Check if this is an imported load (has stored pallet data)
      if (ongoingLoad.isImported && ongoingLoad.palletData) {
        // Restore imported load directly from localStorage data
        const pallets = ongoingLoad.palletData;
        
        const arrangementFromSaved = pallets.map((p, idx) => ({
          ...p,
          locked: ongoingLoad.placedPallets?.includes(p.id),
        }));

        const stacksLocal = organizeIntoStacks(arrangementFromSaved);
        
        // Restore problem pallets
        const problemPallets = (ongoingLoad.problemPallets || []).map(pp => {
          const pallet = arrangementFromSaved.find(p => p.id === pp.id);
          return pallet ? { ...pallet, reason: pp.reason } : null;
        }).filter(Boolean);

        // Find where to resume
        const firstUnplaced = findFirstUnplacedPosition(stacksLocal, ongoingLoad.placedPallets || []);

        const plan = {
          planId: ongoingLoad.planId,
          planName: ongoingLoad.planName,
          pallets: arrangementFromSaved,
          proposedOrder: arrangementFromSaved.map(p => p.id),
          currentOrder: arrangementFromSaved.map(p => p.id),
          placedPallets: ongoingLoad.placedPallets || [],
          problemPallets,
          stacks: stacksLocal,
          truckDimensions: ongoingLoad.truckDimensions || { width: 2.45, depth: 12, height: 2.6 },
          hasExistingArrangement: true,
          progress: {
            currentStackIndex: firstUnplaced.stackIndex,
            currentPalletInStack: firstUnplaced.palletInStack,
            totalStacks: stacksLocal.length,
            totalPallets: arrangementFromSaved.length,
            placedCount: ongoingLoad.placedPallets?.length || 0
          }
        };

        setPlanData(plan);
        setSelectedPlan(null); // No savedLoad for imports
        setCurrentPhase('loading');
        return;
      }

      // For non-imported loads, find in saved loads
      const savedLoad = recentLoads.find(l => String(l.id) === String(ongoingLoad.planId));
      
      if (!savedLoad) {
        setShowAllDates(true);
        setError('Load not found in recent loads. Try selecting "Show all recent loads".');
        setLoading(false);
        return;
      }

      const pallets = savedLoad.pallets_data || [];
      const truckData = savedLoad.truck_data || {};
      
      if (pallets.length === 0) {
        throw new Error('No pallets found in selected load');
      }

      const arrangementFromSaved = pallets.map((p, idx) => ({
        id: p.id,
        name: p.name || `Pallet${p.id}`,
        dimensions: p.dimensions,
        weight: p.weight || 0,
        attribute: p.attribute || 'none',
        notToploadable: Boolean(p.notToploadable),
        rotation: p.rotation || 0,
        position: Array.isArray(p.position) || (p.position && typeof p.position === 'object') ? p.position : undefined,
        locked: ongoingLoad.placedPallets?.includes(p.id),
        unit_id: 1,
        __origIndex: idx,
        imageUrl: p.imageUrl || p.image_url,
        labelImageUrl: p.labelImageUrl || p.label_image_url,
        destination: p.destination,
        shipment_id: p.shipment_id,
      }));

      const stacksLocal = organizeIntoStacks(arrangementFromSaved);
      
      // Restore problem pallets
      const problemPallets = (ongoingLoad.problemPallets || []).map(pp => {
        const pallet = arrangementFromSaved.find(p => p.id === pp.id);
        return pallet ? { ...pallet, reason: pp.reason } : null;
      }).filter(Boolean);

      // Find where to resume
      const firstUnplaced = findFirstUnplacedPosition(stacksLocal, ongoingLoad.placedPallets || []);

      const plan = {
        planId: savedLoad.id,
        pallets: arrangementFromSaved,
        proposedOrder: arrangementFromSaved.map(p => p.id),
        currentOrder: arrangementFromSaved.map(p => p.id),
        placedPallets: ongoingLoad.placedPallets || [],
        problemPallets,
        stacks: stacksLocal,
        truckDimensions: truckData.dimensions || { width: 2.45, depth: 12, height: 2.6 },
        hasExistingArrangement: true,
        progress: {
          currentStackIndex: firstUnplaced.stackIndex,
          currentPalletInStack: firstUnplaced.palletInStack,
          totalStacks: stacksLocal.length,
          totalPallets: arrangementFromSaved.length,
          placedCount: ongoingLoad.placedPallets?.length || 0
        }
      };

      setPlanData(plan);
      setSelectedPlan(savedLoad);
      setCurrentPhase('loading');

    } catch (err) {
      setError(err.message || 'Failed to resume load');
      console.error('Error resuming load:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentLoads]);

  /**
   * Remove an ongoing load from the list
   */
  const handleRemoveOngoingLoad = useCallback((planId) => {
    setOngoingLoads(prev => {
      const filtered = prev.filter(l => l.planId !== planId);
      localStorage.setItem('lodia_ongoing_loads', JSON.stringify(filtered));
      return filtered;
    });
  }, []);

  /**
   * Find the first unplaced pallet position in stacks
   * Returns { stackIndex, palletInStack } for the first pallet not in placedPallets
   */
  const findFirstUnplacedPosition = useCallback((stacks, placedPalletIds) => {
    const placedSet = new Set(placedPalletIds || []);
    for (let stackIndex = 0; stackIndex < stacks.length; stackIndex++) {
      const stack = stacks[stackIndex];
      for (let palletInStack = 0; palletInStack < stack.pallets.length; palletInStack++) {
        const pallet = stack.pallets[palletInStack];
        if (!placedSet.has(pallet.id)) {
          return { stackIndex, palletInStack };
        }
      }
    }
    // All placed - return end position
    return { stackIndex: stacks.length, palletInStack: 0 };
  }, []);

  /**
   * Organize pallets into stacks based on X position (right to left)
   */
  const organizeIntoStacks = (pallets) => {
    // Group pallets by row (Z) then by column (X). Order: rows front->back (high Z->low Z), columns right->left (high X->low X).
    const rowMap = new Map(); // zPos -> Map(xPos -> pallets[])

    pallets.forEach((pallet, index) => {
      // Extract positions supporting both array [x,y,z] and object {x,y,z}
      let xRaw = 0, yRaw = 0, zRaw = 0;
      if (Array.isArray(pallet.position)) {
        xRaw = pallet.position?.[0] ?? 0;
        yRaw = pallet.position?.[1] ?? 0;
        zRaw = pallet.position?.[2] ?? 0;
      } else if (pallet.position && typeof pallet.position === 'object') {
        xRaw = pallet.position.x ?? 0;
        yRaw = pallet.position.y ?? 0;
        zRaw = pallet.position.z ?? 0;
      }
      const xPos = Math.round((xRaw || 0) * 10) / 10; // 0.1m buckets across width
      const zPos = Math.round((zRaw || 0) * 10) / 10; // 0.1m buckets along depth

      if (!rowMap.has(zPos)) rowMap.set(zPos, new Map());
      const colMap = rowMap.get(zPos);
      if (!colMap.has(xPos)) colMap.set(xPos, []);
      colMap.get(xPos).push({
        ...pallet,
        stackOrder: index,
        originalIndex: index,
        __y: yRaw
      });
    });

  // Sort rows front to back. If your solver/scene uses lower Z for front, use ascending (low Z first).
  const rowsSorted = Array.from(rowMap.entries()).sort(([zA], [zB]) => zA - zB);

    // Emit stacks in desired traversal: for each row, from right (high X) to left (low X)
    const result = [];
    let stackIndexCounter = 0;
    for (const [zPos, colMap] of rowsSorted) {
      const colsSorted = Array.from(colMap.entries()).sort(([xA], [xB]) => xB - xA);
      for (const [xPos, items] of colsSorted) {
        // Within a physical stack (same X,Z), load bottom to top: sort by Y ascending; tie-breaker by original index
        const palletsSorted = items.sort((a, b) => (a.__y - b.__y) || (a.stackOrder - b.stackOrder)).map(p => {
          const { __y, ...rest } = p; return rest;
        });
        result.push({
          stackIndex: stackIndexCounter++,
          xPosition: xPos,
          zPosition: zPos,
          pallets: palletsSorted
        });
      }
    }
    return result;
  };

  // ==================== SOLVER INTEGRATION ====================
  
  /**
   * Call solver with current state (placed pallets as locked, problem pallets excluded)
   */
  // New function that takes filtered pallets directly (avoids state timing issues)
  const runSolverWithFilteredPallets = useCallback(async (filteredPallets) => {
    setLoading(true);
    setError(null);
    try {
      console.log(`🚨 [SOLVER DEBUG] Running solver with directly filtered pallets:`, {
        totalPallets: filteredPallets.length,
        palletIds: filteredPallets.slice(0, 3).map(p => p.id)
      });
      
      const unplacedPallets = filteredPallets.filter(p => 
        !planData.placedPallets.includes(p.id)
      );

      const lockedPallets = filteredPallets.filter(p => 
        planData.placedPallets.includes(p.id)
      );
      
      console.log(`🚨 [SOLVER DEBUG] Filtered pallets:`, {
        unplaced: unplacedPallets.length,
        locked: lockedPallets.length,
        unplacedIds: unplacedPallets.slice(0, 3).map(p => p.id),
        lockedIds: lockedPallets.slice(0, 3).map(p => p.id)
      });

      const solverPayload = {
        pallets: [
          ...lockedPallets.map(p => ({
            id: p.id,
            name: p.name || `Pallet${p.id}`,
            dimensions: p.dimensions,
            weight: p.weight || 0,
            attribute: p.attribute || 'none',
            notToploadable: Boolean(p.notToploadable),
            locked: true,
            position: p.position ? [...p.position] : [0, 0, 0],
            rotation: p.rotation || 0,
            unit_id: 1,
            // Preserve image URLs through solver
            imageUrl: p.imageUrl,
            labelImageUrl: p.labelImageUrl,
            destination: p.destination,
            shipment_id: p.shipment_id,
          })),
          ...unplacedPallets.map(p => ({
            id: p.id,
            name: p.name || `Pallet${p.id}`,
            dimensions: p.dimensions,
            weight: p.weight || 0,
            attribute: p.attribute || 'none',
            notToploadable: Boolean(p.notToploadable),
            locked: false,
            unit_id: 1,
            // Preserve image URLs through solver
            imageUrl: p.imageUrl,
            labelImageUrl: p.labelImageUrl,
            destination: p.destination,
            shipment_id: p.shipment_id,
          }))
        ],
        truckDimensions: planData.truckDimensions || { width: 2.45, depth: 12, height: 2.6 },
        vehicleId: 1,
        resetRows: false,
        solverVersion: 'stacked_v2'
      };

      console.log(`🚨 [SOLVER DEBUG] Solver payload has ${solverPayload.pallets.length} pallets`);
      
      let applied = false;

      // Always try primary endpoint (prefer /api path for proxy)
      try {
        const response = await authenticatedFetch('/api/arrange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(solverPayload)
        });
        if (response.ok) {
          const result = await response.json();
          if (result && result.arrangement) {
            // Merge solver result with original pallets to preserve image URLs
            const originalPallets = solverPayload.pallets;
            const mergedArrangement = result.arrangement.map(p => {
              const original = originalPallets.find(op => op.id === p.id);
              return {
                ...p,
                imageUrl: p.imageUrl || original?.imageUrl,
                labelImageUrl: p.labelImageUrl || original?.labelImageUrl,
                destination: p.destination || original?.destination,
                shipment_id: p.shipment_id || original?.shipment_id,
              };
            });
            const newStacks = organizeIntoStacks(mergedArrangement);
            const firstUnplaced = findFirstUnplacedPosition(newStacks, planData.placedPallets);
            setPlanData(prev => ({
              ...prev,
              pallets: mergedArrangement,
              proposedOrder: mergedArrangement.map(p => p.id),
              currentOrder: mergedArrangement.map(p => p.id),
              stacks: newStacks,
              progress: {
                ...prev.progress,
                totalStacks: newStacks.length,
                totalPallets: mergedArrangement.length,
                currentStackIndex: firstUnplaced.stackIndex,
                currentPalletInStack: firstUnplaced.palletInStack
              }
            }));
            console.log(`🚨 [SOLVER DEBUG] Updated plan with ${mergedArrangement.length} pallets, resuming at stack ${firstUnplaced.stackIndex}`);
            applied = true;
          }
        }
      } catch (e1) {
        console.warn('[SOLVER] Primary endpoint failed for filtered rerun, will try alternate.', e1?.message || e1);
      }
      if (!applied) {
        try {
          const response2 = await authenticatedFetch('/arrange', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(solverPayload)
          });
          if (response2.ok) {
            const result2 = await response2.json();
            if (result2 && result2.arrangement) {
              // Merge solver result with original pallets to preserve image URLs
              const originalPallets = solverPayload.pallets;
              const mergedArrangement2 = result2.arrangement.map(p => {
                const original = originalPallets.find(op => op.id === p.id);
                return {
                  ...p,
                  imageUrl: p.imageUrl || original?.imageUrl,
                  labelImageUrl: p.labelImageUrl || original?.labelImageUrl,
                  destination: p.destination || original?.destination,
                  shipment_id: p.shipment_id || original?.shipment_id,
                };
              });
              const newStacks2 = organizeIntoStacks(mergedArrangement2);
              const firstUnplaced2 = findFirstUnplacedPosition(newStacks2, planData.placedPallets);
              setPlanData(prev => ({
                ...prev,
                pallets: mergedArrangement2,
                proposedOrder: mergedArrangement2.map(p => p.id),
                currentOrder: mergedArrangement2.map(p => p.id),
                stacks: newStacks2,
                progress: {
                  ...prev.progress,
                  totalStacks: newStacks2.length,
                  totalPallets: mergedArrangement2.length,
                  currentStackIndex: firstUnplaced2.stackIndex,
                  currentPalletInStack: firstUnplaced2.palletInStack
                }
              }));
              console.log(`🚨 [SOLVER DEBUG] Updated plan (alt) with ${mergedArrangement2.length} pallets, resuming at stack ${firstUnplaced2.stackIndex}`);
              applied = true;
            }
          }
        } catch (e2) {
          console.warn('[SOLVER] Alternate endpoint failed for filtered rerun; will fallback offline.', e2?.message || e2);
        }
      }

      // Fallback: offline replan using filteredPallets
      if (!applied) {
        const newStacks = organizeIntoStacks(filteredPallets);
        const firstUnplacedFallback = findFirstUnplacedPosition(newStacks, planData.placedPallets);
        setPlanData(prev => ({
          ...prev,
          pallets: filteredPallets,
          proposedOrder: filteredPallets.map(p => p.id),
          currentOrder: filteredPallets.map(p => p.id),
          stacks: newStacks,
          progress: {
            ...prev.progress,
            totalStacks: newStacks.length,
            totalPallets: filteredPallets.length,
            currentStackIndex: firstUnplacedFallback.stackIndex,
            currentPalletInStack: firstUnplacedFallback.palletInStack
          }
        }));
        console.info('[SOLVER] Offline fallback applied for filtered rerun.');
      }
    } catch (e) {
      console.error('[NEW LOADER] Solver failed:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [planData.placedPallets, planData.truckDimensions, authenticatedFetch, organizeIntoStacks, findFirstUnplacedPosition, isBackendConfigured]);

  const runSolverWithCurrentState = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`🚨 [SOLVER DEBUG] Starting solver with:`, {
        totalPallets: planData.pallets.length,
        placedPallets: planData.placedPallets.length,
        problemPallets: planData.problemPallets.length,
        problemPalletIds: planData.problemPallets.map(p => p.id)
      });
      
      const unplacedPallets = planData.pallets.filter(p => 
        !planData.placedPallets.includes(p.id) && 
        !planData.problemPallets.some(pp => pp.id === p.id)
      );

      const lockedPallets = planData.pallets.filter(p => 
        planData.placedPallets.includes(p.id)
      );
      
      console.log(`🚨 [SOLVER DEBUG] Filtered pallets:`, {
        unplaced: unplacedPallets.length,
        locked: lockedPallets.length,
        unplacedIds: unplacedPallets.slice(0, 3).map(p => p.id),
        lockedIds: lockedPallets.slice(0, 3).map(p => p.id)
      });

      const solverPayload = {
        pallets: [
          ...lockedPallets.map(p => ({
            id: p.id,
            name: p.name || `Pallet${p.id}`,
            dimensions: p.dimensions,
            weight: p.weight || 0,
            attribute: p.attribute || 'none',
            notToploadable: Boolean(p.notToploadable),
            locked: true,
            position: p.position ? [...p.position] : [0, 0, 0],
            rotation: p.rotation || 0,
            unit_id: 1
          })),
          ...unplacedPallets.map(p => ({
            id: p.id,
            name: p.name || `Pallet${p.id}`,
            dimensions: p.dimensions,
            weight: p.weight || 0,
            attribute: p.attribute || 'none',
            notToploadable: Boolean(p.notToploadable),
            locked: false,
            unit_id: 1
          }))
        ],
        truckDimensions: planData.truckDimensions,
        vehicleId: 1,
        resetRows: false, // Don't reset already placed pallets
        solverVersion: 'stacked_v2'
      };

      let applied = false;
      try {
        const response = await authenticatedFetch('/api/arrange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(solverPayload)
        });
        if (response.ok) {
          const result = await response.json();
          if (result?.arrangement) {
            const newStacks = organizeIntoStacks(result.arrangement);
            const firstUnplaced = findFirstUnplacedPosition(newStacks, planData.placedPallets);
            const newOrder = result.arrangement
              .filter(p => !planData.placedPallets.includes(p.id) && !planData.problemPallets.some(pp => pp.id === p.id))
              .map(p => p.id);
            setPlanData(prev => {
              return {
                ...prev,
                pallets: result.arrangement,
                currentOrder: newOrder,
                stacks: newStacks,
                progress: {
                  ...prev.progress,
                  currentStackIndex: firstUnplaced.stackIndex,
                  currentPalletInStack: firstUnplaced.palletInStack,
                  totalStacks: newStacks.length,
                  totalPallets: result.arrangement.length
                }
              };
            });
            console.log(`Solver rerun completed (primary), resuming at stack ${firstUnplaced.stackIndex}.`);
            applied = true;
          }
        }
      } catch (e1) {
        console.warn('[SOLVER] Current-state primary endpoint failed; trying alternate.', e1?.message || e1);
      }
      if (!applied) {
        try {
          const response2 = await authenticatedFetch('/arrange', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(solverPayload)
          });
          if (response2.ok) {
            const result2 = await response2.json();
            if (result2?.arrangement) {
              const newStacks2 = organizeIntoStacks(result2.arrangement);
              const firstUnplaced2 = findFirstUnplacedPosition(newStacks2, planData.placedPallets);
              const newOrder2 = result2.arrangement
                .filter(p => !planData.placedPallets.includes(p.id) && !planData.problemPallets.some(pp => pp.id === p.id))
                .map(p => p.id);
              setPlanData(prev => {
                return {
                  ...prev,
                  pallets: result2.arrangement,
                  currentOrder: newOrder2,
                  stacks: newStacks2,
                  progress: {
                    ...prev.progress,
                    currentStackIndex: firstUnplaced2.stackIndex,
                    currentPalletInStack: firstUnplaced2.palletInStack,
                    totalStacks: newStacks2.length,
                    totalPallets: result2.arrangement.length
                  }
                };
              });
              console.log(`Solver rerun completed (alternate), resuming at stack ${firstUnplaced2.stackIndex}.`);
              applied = true;
            }
          }
        } catch (e2) {
          console.warn('[SOLVER] Current-state alternate endpoint failed; using offline fallback.', e2?.message || e2);
        }
      }

      // Offline fallback: recompute stacks/order from existing pallets
      if (!applied) {
        const existing = planData.pallets || [];
        const newStacks = organizeIntoStacks(existing);
        const firstUnplacedFallback = findFirstUnplacedPosition(newStacks, planData.placedPallets);
        const newOrder = existing
          .filter(p => !planData.placedPallets.includes(p.id) && !planData.problemPallets.some(pp => pp.id === p.id))
          .map(p => p.id);
        setPlanData(prev => ({
          ...prev,
          currentOrder: newOrder,
          stacks: newStacks,
          progress: {
            ...prev.progress,
            currentStackIndex: firstUnplacedFallback.stackIndex,
            currentPalletInStack: firstUnplacedFallback.palletInStack,
            totalStacks: newStacks.length,
            totalPallets: existing.length
          }
        }));
        console.info('[SOLVER] Offline fallback applied for current-state rerun.');
      }

    } catch (err) {
      setError(err.message || 'Failed to run solver');
      console.error('Solver error:', err);
    } finally {
      setLoading(false);
    }
  }, [planData, authenticatedFetch, organizeIntoStacks, findFirstUnplacedPosition, isBackendConfigured]);

  // ==================== PHASE HANDLERS ====================
  
  const handleSelectPlan = (savedLoad) => {
    processSavedLoadToPlan(savedLoad);
  };

  const handleValidationComplete = () => {
    setCurrentPhase('loading');
  };

  // Handle completion of precheck: remove marked pallets and run solver
  const handlePrecheckComplete = useCallback(async (issuesMap) => {
    const problemIds = Object.entries(issuesMap)
      .filter(([, reason]) => !!reason)
      .map(([id]) => (isNaN(id) ? id : Number(id)));

    const problemPallets = Object.entries(issuesMap)
      .filter(([, reason]) => !!reason)
      .map(([id, reason]) => ({ id: isNaN(id) ? id : Number(id), reason, timestamp: Date.now() }));

    setPlanData(prev => ({ ...prev, problemPallets }));

    const filtered = (planData?.pallets || []).filter(p => !problemIds.includes(p.id));

    const hadExisting = !!planData?.hasExistingArrangement;
    const anyIssues = problemIds.length > 0;

    if (!hadExisting) {
      // No prior arrangement: we must compute one via solver.
      await runSolverWithFilteredPallets(filtered);
    } else if (anyIssues) {
      // There is an existing arrangement, but issues were marked: rerun solver with remaining pallets.
      await runSolverWithFilteredPallets(filtered);
    } else {
      // Existing arrangement and no issues: keep as-is, no solver call.
      setPlanData(prev => ({ ...prev, pallets: filtered, stacks: organizeIntoStacks(filtered) }));
    }

    setCurrentPhase('loading');
  }, [planData?.pallets, planData?.hasExistingArrangement, runSolverWithFilteredPallets, organizeIntoStacks]);

  // Gate manual replan: allow only if issues exist
  const guardedRerunSolver = useCallback(async () => {
    const hasIssues = (planData?.problemPallets || []).length > 0;
    if (!hasIssues) {
      setError('Replan is only available after marking issues on pallets.');
      return;
    }
    await runSolverWithCurrentState();
  }, [planData?.problemPallets, runSolverWithCurrentState]);

  const handlePalletMarkedProblem = async (palletId, reason) => {
    console.log('🚨 [NEW LOADER DEBUG] handlePalletMarkedProblem called:', { palletId, reason, planId: planData.planId });
    
    if (reason === null) {
      // Clearing the problem - add pallet back (we'd need to restore from somewhere)
      console.log('🚨 [NEW LOADER DEBUG] Clearing problem - not implemented yet');
      return;
    }
    
    // SIMPLE APPROACH: Just remove the pallet from the pallets list completely
    setPlanData(prev => {
      const filteredPallets = prev.pallets.filter(p => p.id !== palletId);
      
      console.log('🚨 [NEW LOADER DEBUG] REMOVING PALLET:', {
        before: prev.pallets.length,
        after: filteredPallets.length,
        removedId: palletId,
        remainingIds: filteredPallets.slice(0, 3).map(p => p.id)
      });
      
      return {
        ...prev,
        pallets: filteredPallets,
        proposedOrder: filteredPallets.map(p => p.id),
        currentOrder: filteredPallets.map(p => p.id),
        problemPallets: [
          ...prev.problemPallets.filter(p => p.id !== palletId),
          { id: palletId, reason, timestamp: Date.now() }
        ]
      };
    });
    
    // Now run the solver with the remaining pallets
    // Use setState callback to ensure we use the updated state
    setPlanData(prev => {
      const filteredPallets = prev.pallets.filter(p => p.id !== palletId);
      
      // Trigger solver with the filtered pallets directly (not relying on state timing)
      setTimeout(() => {
        console.log(`🚨 [NEW LOADER DEBUG] Running solver with ${filteredPallets.length} filtered pallets`);
        runSolverWithFilteredPallets(filteredPallets);
      }, 50);
      
      return prev; // Don't update state again, already updated above
    });
    
    // Also call backend to record the exclusion (but don't wait for it)
    if (planData.planId && isBackendConfigured()) {
      try {
        const apiUrl = `/load-plans/${planData.planId}/exceptions?replan=1`;
        authenticatedFetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ palletId, reason })
        }).then(() => {
          console.log(`🚨 [NEW LOADER DEBUG] Backend exclusion recorded successfully`);
        }).catch(e => {
          console.warn('[NEW LOADER] Backend exclusion failed (but pallet already removed):', e);
        });
      } catch (e) {
        console.warn('[NEW LOADER] Backend exclusion error:', e);
      }
    }
  };

  const handlePalletPlaced = (palletId) => {
    setPlanData(prev => {
      const newPlacedPallets = [...prev.placedPallets, palletId];
      const newProgress = {
        ...prev.progress,
        placedCount: newPlacedPallets.length
      };

      // Check if we need to move to next stack
      const currentStack = prev.stacks[prev.progress.currentStackIndex];
      if (currentStack) {
        const stackPalletsRemaining = currentStack.pallets.filter(p => 
          !newPlacedPallets.includes(p.id) && 
          !prev.problemPallets.some(pp => pp.id === p.id)
        ).length;

        if (stackPalletsRemaining === 0 && prev.progress.currentStackIndex < prev.stacks.length - 1) {
          newProgress.currentStackIndex += 1;
          newProgress.currentPalletInStack = 0;
        }
      }

      return {
        ...prev,
        placedPallets: newPlacedPallets,
        progress: newProgress
      };
    });

    // Check if loading is complete
    const totalUnproblemPallets = planData.pallets.length - planData.problemPallets.length;
    if (planData.placedPallets.length + 1 >= totalUnproblemPallets) {
      setCurrentPhase('complete');
    }
  };

  const handleBackToValidation = () => {
    setCurrentPhase('validation');
  };

  // ==================== RENDER METHODS ====================
  
  const renderMobileHeader = () => (
    <div className="bg-white border-b border-gray-200 px-3 py-3 flex items-center justify-between">
      <div className="flex items-center space-x-2">
        {/* Main app sidebar trigger */}
        <SidebarTrigger className="flex-shrink-0" />
        <div className="min-w-0">
          <h1 className="text-base font-semibold truncate">Loader</h1>
          <p className="text-xs text-gray-500 truncate">
            {currentPhase === 'selection' && 'Select Plan'}
            {currentPhase === 'precheck' && 'Pre-check Pallets'}
            {currentPhase === 'validation' && 'Validate Pallets'}
            {currentPhase === 'loading' && 'Loading Progress'}
            {currentPhase === 'complete' && 'Loading Complete'}
          </p>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        {planData.planId && (
          <LoaderProgressTracker 
            progress={planData.progress}
            compact={true}
          />
        )}
        {/* Menu toggle for loader sidebar (when in loading/validation phase) */}
        {(currentPhase === 'validation' || currentPhase === 'loading') && planData.planId && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex-shrink-0"
          >
            <Menu className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderPlanSelection = () => (
    <div className="p-4 space-y-4">
      {/* Ongoing Loads Section - Prominent at top */}
      {ongoingLoads.length > 0 && (
        <Card className="border-2 border-amber-400 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center space-x-2 text-amber-800">
              <Loader2 className="h-5 w-5" />
              <span>Continue Loading</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {ongoingLoads.map((ongoing) => (
              <div
                key={ongoing.planId}
                className="bg-white border border-amber-300 rounded-lg p-3 cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => handleResumeOngoingLoad(ongoing)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <h3 className="font-medium text-gray-900">{ongoing.planName}</h3>
                      <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                        In Progress
                      </span>
                      {ongoing.isImported && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Imported
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {ongoing.placedPallets?.length || 0} / {ongoing.totalPallets} pallets loaded
                    </p>
                    <div className="w-full bg-amber-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-amber-500 h-2 rounded-full transition-all"
                        style={{ width: `${((ongoing.placedPallets?.length || 0) / ongoing.totalPallets) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Last updated: {new Date(ongoing.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col items-end space-y-1 ml-3">
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      onClick={(e) => { e.stopPropagation(); handleResumeOngoingLoad(ongoing); }}
                    >
                      <ArrowRight className="h-4 w-4 mr-1" />
                      Resume
                    </Button>
                    <button
                      className="text-xs text-gray-500 hover:text-red-600"
                      onClick={(e) => { e.stopPropagation(); handleRemoveOngoingLoad(ongoing.planId); }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Truck className="h-5 w-5" />
              <span>Select Load Plan</span>
            </div>
            {/* Import Dropdown */}
            <div className="relative" ref={importDropdownRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportDropdown(!showImportDropdown)}
                className="flex items-center space-x-1"
              >
                <Upload className="h-4 w-4" />
                <span>Import</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
              
              {showImportDropdown && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <button
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-t-lg"
                    onClick={() => {
                      setShowImportDropdown(false);
                      fileInputRef.current?.click();
                    }}
                  >
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    <span>From Excel File</span>
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-b-lg"
                    onClick={openScannerImportModal}
                  >
                    <Camera className="h-4 w-4 text-blue-600" />
                    <span>From Pallet Scanner</span>
                  </button>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleExcelImport}
              className="hidden"
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Selection */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const d = new Date(selectedDate);
                d.setDate(d.getDate() - 1);
                setSelectedDate(d.toISOString().slice(0, 10));
              }}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
            />
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const d = new Date(selectedDate);
                d.setDate(d.getDate() + 1);
                setSelectedDate(d.toISOString().slice(0, 10));
              }}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="showAll"
              checked={showAllDates}
              onChange={(e) => setShowAllDates(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="showAll" className="text-sm">Show all recent loads</label>
          </div>

          {/* Load List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recentLoads.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No loads found for selected date</p>
            ) : (
              recentLoads.map((load) => (
                <div
                  key={load.id}
                  className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleSelectPlan(load)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium">{load.name || `Load ${load.id}`}</h3>
                      <p className="text-sm text-gray-500">
                        {resolveLineName(load.line_id)} → {resolveTruckName(load.line_id, load.truck_id)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {(load.pallets_data || []).length} pallets
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{load.load_date || load.date}</p>
                      <ArrowRight className="h-4 w-4 text-gray-400 mt-1" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Scanner Import Modal */}
      {showScannerImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Camera className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold">Import from Pallet Scanner</h2>
              </div>
              <button
                onClick={() => setShowScannerImportModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, destination, shipment ID..."
                  value={scannerSearchQuery}
                  onChange={(e) => setScannerSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              {/* Filter and selection controls */}
              <div className="flex items-center justify-between mt-3">
                {/* Unassigned filter toggle */}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOnlyUnassigned}
                    onChange={(e) => setShowOnlyUnassigned(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Show only unassigned ({unassignedCount})
                  </span>
                </label>
                <button
                  onClick={selectAllFiltered}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Select all ({filteredScannedPallets.length})
                </button>
              </div>
            </div>

            {/* Pallet List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingScannedPallets ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading pallets...</span>
                </div>
              ) : filteredScannedPallets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No {showOnlyUnassigned ? 'unassigned ' : ''}pallets found</p>
                  <p className="text-sm">
                    {showOnlyUnassigned 
                      ? 'All pallets have been assigned to loads' 
                      : 'Scan some pallets first in the Pallet Scanner tab'}
                  </p>
                  {showOnlyUnassigned && scannedPallets.length > 0 && (
                    <button
                      onClick={() => setShowOnlyUnassigned(false)}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      Show all pallets
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredScannedPallets.map((pallet) => (
                    <div
                      key={pallet.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedScannedPallets.has(pallet.id)
                          ? 'border-blue-500 bg-blue-50'
                          : pallet.isAssigned
                            ? 'border-gray-200 bg-gray-50 opacity-60'
                            : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => togglePalletSelection(pallet.id)}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Checkbox */}
                        <div className="mt-0.5">
                          {selectedScannedPallets.has(pallet.id) ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                        </div>

                        {/* Thumbnail */}
                        {(pallet.imageUrl || pallet.labelImageUrl) && (
                          <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-gray-100">
                            <img
                              src={pallet.imageUrl || pallet.labelImageUrl}
                              alt={pallet.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium truncate">{pallet.name}</h4>
                            {pallet.isAssigned && (
                              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0">
                                Assigned
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {pallet.width_cm}×{pallet.depth_cm}×{pallet.height_cm} cm • {pallet.weight_kg}kg
                          </p>
                          {pallet.destination && (
                            <p className="text-sm text-blue-600 truncate">→ {pallet.destination}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex flex-col gap-3">
              {/* Quick action: Load All Unassigned */}
              {unassignedCount > 0 && (
                <Button
                  variant="outline"
                  onClick={loadAllUnassigned}
                  disabled={loading}
                  className="w-full border-green-500 text-green-700 hover:bg-green-50"
                >
                  <Package className="h-4 w-4 mr-2" />
                  Load All Unassigned ({unassignedCount} pallets)
                </Button>
              )}
              
              {/* Selection-based actions */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedScannedPallets.size} pallets selected
                </span>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowScannerImportModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={importSelectedScannedPallets}
                    disabled={selectedScannedPallets.size === 0 || loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Plan...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Create Load ({selectedScannedPallets.size})
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ==================== MAIN RENDER ====================
  
  return (
    <div className="h-screen flex flex-col bg-gray-50 max-w-full overflow-x-hidden">
      {isMobile && renderMobileHeader()}
      
      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Sidebar - always visible on desktop for validation/loading phases */}
        {!isMobile && (currentPhase === 'selection' || currentPhase === 'validation' || currentPhase === 'loading') && (
          <div className="w-80 border-r border-gray-200 bg-white overflow-y-auto flex-shrink-0">
            {currentPhase === 'selection' && renderPlanSelection()}
            
            {(currentPhase === 'validation' || currentPhase === 'loading') && planData.planId && (
              <LoaderPalletList
                planData={planData}
                onPalletMarkedProblem={handlePalletMarkedProblem}
                onValidationComplete={handleValidationComplete}
                onBackToValidation={handleBackToValidation}
                currentPhase={currentPhase}
                isMobile={false}
              />
            )}
          </div>
        )}

        {/* Mobile Sidebar Overlay - for pallet list during loading/validation */}
        {isMobile && sidebarOpen && (currentPhase === 'validation' || currentPhase === 'loading') && planData.planId && (
          <div className="fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <div className="relative w-80 max-w-[85vw] bg-white overflow-y-auto shadow-xl">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold">Pallet List</h2>
                <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <LoaderPalletList
                planData={planData}
                onPalletMarkedProblem={handlePalletMarkedProblem}
                onValidationComplete={handleValidationComplete}
                onBackToValidation={handleBackToValidation}
                currentPhase={currentPhase}
                isMobile={true}
              />
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {loading && (
            <div className="p-3 bg-blue-50 border-b border-blue-200">
              <div className="flex items-center space-x-2 text-blue-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Processing...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border-b border-red-200">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm flex-1 truncate">{error}</span>
                <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Mobile: Show plan selection directly in main content area */}
          {currentPhase === 'selection' && isMobile && (
            <div className="flex-1 overflow-y-auto">
              {renderPlanSelection()}
            </div>
          )}

          {/* Desktop: Show placeholder when no plan selected */}
          {currentPhase === 'selection' && !isMobile && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-600">Select a Load Plan</h2>
                <p className="text-gray-500">Choose a saved load from the sidebar to begin loading</p>
              </div>
            </div>
          )}

          {currentPhase === 'precheck' && (
            <PalletPrecheckView
              pallets={planData?.pallets || []}
              truckDimensions={planData?.truckDimensions || { width: 2.45, depth: 12, height: 2.6 }}
              onComplete={handlePrecheckComplete}
              onCancel={() => setCurrentPhase('selection')}
            />
          )}

          {(currentPhase === 'validation' || currentPhase === 'loading' || currentPhase === 'complete') && planData.planId && (
            <Loader3DView
              planData={planData}
              selectedPalletId={selectedPalletId}
              setSelectedPalletId={setSelectedPalletId}
              onPalletPlaced={handlePalletPlaced}
              onPalletMarkedProblem={handlePalletMarkedProblem}
              currentPhase={currentPhase}
              onRerunSolver={guardedRerunSolver}
              isMobile={isMobile}
            />
          )}
        </div>
      </div>
    </div>
  );
}